import React, { useCallback, useMemo, useState } from 'react'

/**
 * Open/close state for the global chat popup.
 *
 * This is deliberately a separate, tiny context mounted OUTSIDE ChatProvider,
 * for three reasons:
 *
 *  1. ChatProvider must read `isOpen` to decide whether a send is a DM or a
 *     room message (see `chatScope` in ChatContext). A child cannot feed a
 *     parent, so this has to sit above it.
 *  2. HeaderCN renders the trigger and must NOT subscribe to ChatProvider,
 *     whose context value is rebuilt on every keystroke and every incoming
 *     message. Here it subscribes to a value that changes twice a session.
 *  3. Popup visibility is ephemeral UI state with no persistence and no
 *     cross-slice consumers, so it does not earn a Redux slice.
 */

export type ChatPopupView = 'list' | 'thread' | 'compose'

/**
 * A room the popup was opened FOR, explicitly.
 *
 * The popup is otherwise always the global DM chat, deliberately (see
 * `chatScope` in ChatContext). The field app has no room chat of its own to
 * open instead, so a project conversation has to be reachable through the
 * popup, and the only safe way to do that is for the caller to name the room
 * rather than have it inferred from whatever page is behind the popup.
 *
 * `locationId` is carried alongside `projectId` because a project's media is
 * filed in its parent location's image library (there is no project image
 * API), and an attachment needs that id to be filed at all.
 */
export interface ChatPopupRoom {
	projectId?: string
	portfolioId?: string
	locationId?: string
	/** Shown as the popup's title. A room has no DM header to take a name from. */
	label?: string
}

interface ChatPopupContextValue {
	isOpen: boolean
	view: ChatPopupView
	/**
	 * Thread to select once the popup mounts. Set by `open({ eventId })` and
	 * cleared by ChatPopup after it acts on it, so callers such as the Home
	 * dashboard rows can jump straight to a conversation without needing chat
	 * context themselves.
	 */
	pendingEventId: string | null
	/**
	 * The room this popup was opened for, or null for the global DM chat.
	 * Cleared on close, so the next open starts global unless it asks not to.
	 */
	room: ChatPopupRoom | null
	open: (opts?: { eventId?: string; compose?: boolean; room?: ChatPopupRoom }) => void
	close: () => void
	toggle: () => void
	showList: () => void
	showThread: () => void
	showCompose: () => void
	clearPendingEventId: () => void
}

const ChatPopupContext = React.createContext<ChatPopupContextValue | undefined>(undefined)

const ChatPopupProvider = ({ children }: { children: React.ReactNode }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [view, setView] = useState<ChatPopupView>('list')
	const [pendingEventId, setPendingEventId] = useState<string | null>(null)
	const [room, setRoom] = useState<ChatPopupRoom | null>(null)

	const open = useCallback(
		(opts: { eventId?: string; compose?: boolean; room?: ChatPopupRoom } = {}) => {
		setIsOpen(true)
		// Always reassigned, never merged: an open with no room is a request for
		// the global chat, not a request to keep the last room.
		setRoom(opts.room ?? null)
		if (opts.eventId) {
			setPendingEventId(opts.eventId)
			setView('thread')
		} else if (opts.compose) {
			setView('compose')
		} else if (opts.room) {
			// A room IS the conversation, so it opens in it. Landing on the DM
			// list would show one set of conversations while the composer wrote
			// into a different one.
			setView('thread')
		} else {
			setView('list')
		}
		},
		[],
	)

	const close = useCallback(() => {
		setIsOpen(false)
		setPendingEventId(null)
		setRoom(null)
		// Reset to the list so the next open starts where iOS Messages does,
		// rather than resuming a half-finished compose.
		setView('list')
	}, [])

	const toggle = useCallback(() => {
		setIsOpen(prev => {
			if (prev) {
				setPendingEventId(null)
				setRoom(null)
				setView('list')
			}
			return !prev
		})
	}, [])

	const value = useMemo(
		() => ({
			isOpen,
			view,
			pendingEventId,
			room,
			open,
			close,
			toggle,
			// The list is the global DM list, so showing it LEAVES the room.
			// Without this, Back out of a project conversation would show DM
			// threads while the composer still wrote into the project.
			showList: () => {
				setView('list')
				setRoom(null)
			},
			showThread: () => setView('thread'),
			showCompose: () => setView('compose'),
			clearPendingEventId: () => setPendingEventId(null),
		}),
		[isOpen, view, pendingEventId, room, open, close, toggle],
	)

	return <ChatPopupContext.Provider value={value}>{children}</ChatPopupContext.Provider>
}

/**
 * Safe to call outside the provider: returns a closed, inert value rather than
 * throwing. ChatProvider consumes this and is also mounted by Storybook and
 * fixture previews that do not wrap it.
 */
const useChatPopup = (): ChatPopupContextValue => {
	const context = React.useContext(ChatPopupContext)
	return context ?? INERT
}

const noop = () => {}
const INERT: ChatPopupContextValue = {
	isOpen: false,
	view: 'list',
	pendingEventId: null,
	room: null,
	open: noop,
	close: noop,
	toggle: noop,
	showList: noop,
	showThread: noop,
	showCompose: noop,
	clearPendingEventId: noop,
}

export { ChatPopupProvider, useChatPopup }
